import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useTheme } from "styled-components";
import {
  BrokerPortfolioListArea,
  BrokerPortfolioButtonArea,
  NoDataText,
} from "./BrokerPortfolioModal.styles";

import { parseErrorMessage, openNewTab, getBrokerIcon } from "utils";
import { Loader } from "components/Loader";
import { Button } from "components/Button";
import { getBrokerPortfolio, getBrokerOAuthURL } from "services";
import BrokerModal from "shared/Modal/BrokerModal";
import { ListItem } from "components/ListItem";
import { BrokerModalContainer } from "../BrokerModal/BrokerModal.styles";
import { toast } from "Layout/slice";
import { useParams } from "react-router-dom";
import { getPortfoliosByStrategyId } from "features/Dashboard/service";
import { contains, isFreeUser } from "utils/index";
import { Tooltip } from "components/Tooltip/index";

const BrokerPortfolioModal = ({
  show = true,
  skipBrokerModal = false,
  strategyId,
  ...props
}) => {
  const theme = useTheme();
  const { id: urlId } = useParams();
  const id = strategyId || urlId; // Use prop first, fallback to URL param
  const dispatch = useDispatch();
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isBrokerModalVisible, setBrokerModalVisible] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const isFreePlan = isFreeUser();

  const getPortfolios = async () => {
    try {
      setLoading(true);
      let getPortfolios = getPortfoliosByStrategyId(id);
      if (!skipBrokerModal) {
        getPortfolios = getBrokerPortfolio(props.brokerId);
      }
      let _portfolios = await getPortfolios;

      _portfolios = _portfolios.reduce((result, d) => {
        if (d.authentication_status === "confirmed") {
          result.push({
            brokerLogo: getBrokerIcon(d.logo_url, theme.mode),
            selected: false,
            ...d,
          });
        }
        return result;
      }, []);

      const portfolioData = (_portfolios || []).map((portfolio) => ({
        brokerLogo: getBrokerIcon(portfolio.logo_url, theme.mode),
        ...portfolio,
      }));
      setPortfolios(portfolioData);
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPortfolios();
  }, [props.brokerId, skipBrokerModal]);

  const toggleBrokerModal = () => {
    props.onHide();
    props.toggleBrokerModal();
    // setSuccessMessage("Account successfully linked");
  };

  const handleBrokerSelect = async (broker) => {
    try {
      setIsConnecting(true);
      const response = await getBrokerOAuthURL(broker.key);
      if (response?.oauth_url) {
        openNewTab(response.oauth_url);
      } else {
        dispatch(toast({ body: "Account Connected Successfully!" }));
      }
    } catch (error) {
      setIsConnecting(false);
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setIsConnecting(false);
      setBrokerModalVisible(!isBrokerModalVisible);
    }
  };

  const getButtonLabel = () => {
    if (!loading && portfolios.length) {
      return "Connect another account";
    }
    return "Connect account";
  };

  return (
    <>
      <BrokerModalContainer
        show={show}
        onHide={props.onHide}
        headingClass="broker-modal-heading"
        heading={"Please Select Your Broker Account"}
      >
        {loading && <Loader className="portfolio-loader" />}
        {!loading && (
          <>
            <BrokerPortfolioListArea>
              {portfolios.length > 0 ? (
                portfolios.map((portfolio, i) => (
                  <Tooltip
                    key={i}
                    value={
                      isFreePlan && !contains(portfolio.broker_name, "Surmount")
                        ? "To unlock this feature you need to upgrade Surmount premium"
                        : ""
                    }
                    tooltipClassName="v2-tooltip"
                    placement="bottom"
                  >
                    <ListItem
                      key={i}
                      brokerLogo={portfolio.logo_url}
                      broker={
                        !skipBrokerModal
                          ? portfolio.nickname
                          : portfolio.broker_account_nickname
                      }
                      onClick={() => props.handlePortfolioClick(portfolio)}
                      eligible={portfolio.eligible}
                      eligibleMessage={portfolio.eligibility_message}
                      brokerKey={portfolio?.broker_key}
                    />
                  </Tooltip>
                ))
              ) : (
                <NoDataText>No portfolio found</NoDataText>
              )}
            </BrokerPortfolioListArea>
            <BrokerPortfolioButtonArea>
              <Button
                className="broker-btn mt-2"
                onClick={toggleBrokerModal}
                label={getButtonLabel()}
              />
            </BrokerPortfolioButtonArea>
          </>
        )}
      </BrokerModalContainer>
      {isBrokerModalVisible && (
        <BrokerModal
          show={isBrokerModalVisible}
          handleBrokerClick={handleBrokerSelect}
          onHide={() => setBrokerModalVisible(false)}
          isConnecting={isConnecting}
        />
      )}
    </>
  );
};

export default BrokerPortfolioModal;

BrokerPortfolioModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  handlePortfolioClick: PropTypes.func,
  brokerId: PropTypes.string,
  showPortfolioModal: PropTypes.func,
  skipBrokerModal: PropTypes.bool,
  toggleBrokerModal: PropTypes.func,
  strategyId: PropTypes.string,
};

BrokerPortfolioModal.displayName = "BrokerPortfolioModal";
